import React from 'react'
import './mission.css'

const ChatBubbleIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
  </svg>
)

const CompassIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10" />
    <polygon points="16.24 7.76 14.12 14.12 7.76 16.24 9.88 9.88 16.24 7.76" />
  </svg>
)

const LayersIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polygon points="12 2 2 7 12 12 22 7 12 2" />
    <polyline points="2 17 12 22 22 17" />
    <polyline points="2 12 12 17 22 12" />
  </svg>
)

const ZapIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" />
  </svg>
)

const PILLARS = [
  {
    icon: ChatBubbleIcon,
    title: 'Conversational Fluidity',
    desc: 'Traditional apps trap you in rigid mazes of menus, nested dropdowns, and fragile filters. With an agent, dialogue is alive: speak or text in plain language, pivot criteria mid-thought, and explore freely without ever having to click "Reset Search."',
    contrastLabel: 'Traditional Apps & Sites',
    contrastText: 'Static forms, click-heavy filters, and rigid page paths.'
  },
  {
    icon: CompassIcon,
    title: 'Intelligence That Learns You',
    desc: 'Static software serves the exact same generic pixels to everyone. Dionyziuz agents tune in to your frequency—remembering your home breaks, 14er routes, favorite directors, and playing style, calibrating every answer without manual reconfiguration.',
    contrastLabel: 'Traditional Apps & Sites',
    contrastText: 'One-size-fits-all pages requiring repetitive manual inputs.'
  },
  {
    icon: LayersIcon,
    title: 'Synthesis Across the Chaos',
    desc: 'Why juggle five bookmarks, satellite radar feeds, road closure maps, and permit lotteries when one intelligence can connect the dots? Our agents absorb the raw noise of the world and deliver distilled, actionable clarity.',
    contrastLabel: 'Traditional Apps & Sites',
    contrastText: 'Juggling tabs and manually correlating fragmented data.'
  },
  {
    icon: ZapIcon,
    title: 'Action Over Data Management',
    desc: 'Most software treats you as an unpaid data entry clerk. Dionyziuz agents combine conversational reasoning with immediate action—rendering dynamic chessboards, delivering dawn patrol wind alerts, and locking in streaming availability on demand.',
    contrastLabel: 'Traditional Apps & Sites',
    contrastText: 'Passive viewing that leaves execution entirely to the user.'
  }
]

const Mission = () => {
  return (
    <section className="missionSection" id="mission">
      <div className="container">
        {/* Section Header */}
        <div className="missionHeader">
          <span className="missionKicker">Our Mission &amp; Philosophy</span>
          <h2>Why Agents Instead of Apps</h2>
          <p className="missionLead">
            Software should adapt to human thought—not force humans to learn software hierarchies.
            Talking to a specialized AI agent is fundamentally more flexible, context-aware, and fluid
            than clicking through traditional static websites or siloed mobile apps.
          </p>
        </div>

        {/* Dionyziuz Manifesto Card */}
        <div className="missionQuoteCard">
          <div className="quotePill">
            <span className="quotePillIcon" aria-hidden="true">&#9670;</span>
            <span>The Post-App Era</span>
          </div>
          <h3 className="manifestoHeadline">
            Apps turned people into database operators. We&apos;re bringing fluidity back to software.
          </h3>
          <p className="quoteBody">
            Most modern applications are essentially pretty cages for databases. They make you do all the heavy lifting:
            hunting through menus, decoding charts, cross-referencing tabs, and resetting search filters.
            We build agents because conversation is the most fluid, natural interface ever created. An agent doesn&apos;t
            hand you a raw dashboard; it listens, learns your context, synthesizes the noise of the world, and turns
            conversation directly into action.
          </p>
          <div className="quoteMeta">
            <div className="quoteAuthor">
              <span className="quoteAuthorName">The Dionyziuz Philosophy</span>
              <span className="quoteAuthorRole">Specialized AI for the things you love</span>
            </div>
            <div className="quoteTakeaway">
              Living natively in your flow. No app store downloads, no siloed logins, no rigid navigation trees—just
              deep domain intelligence ready when you are.
            </div>
          </div>
        </div>

        {/* 4 Pillars of the Agent Advantage */}
        <div className="missionGrid">
          {PILLARS.map((pillar) => {
            const Icon = pillar.icon
            return (
              <div className="missionCard" key={pillar.title}>
                <div className="missionCardIcon">
                  <Icon />
                </div>
                <h3 className="missionCardTitle">{pillar.title}</h3>
                <p className="missionCardText">{pillar.desc}</p>
                <div className="missionCardContrast">
                  <span className="contrastLabel">{pillar.contrastLabel}</span>
                  {pillar.contrastText}
                </div>
              </div>
            )
          })}
        </div>
      </div>
    </section>
  )
}

export default Mission
